import React, {useCallback, useState} from 'react';
import type {GridSortModel} from '@mui/x-data-grid-pro';
import {useGridApiRef} from '@mui/x-data-grid-pro';
import type {Agent, AgentWorkSummary} from 'services/agent/types';
import {useLocalization} from 'localization';
import {useDataGridSettings} from 'services/settings/hooks';
import {useDataTablePagination} from 'components/dataTable/hooks';
import {Table} from 'schema';
import {agentWorkSummaryTableConfig} from 'schemas';
import {agentWorkSummarySchema} from 'schemas/schemas/agentWorkSummarySchema';
import {useAgentWorks} from '../hooks';

type Props = {
  agent: Pick<Agent, 'id' | 'roles' | 'agentType'> | undefined;
  onRowClick?: (row: AgentWorkSummary) => void;
};

export const AgentWorksTable: React.FC<Props> = ({agent, onRowClick}) => {
  const {t} = useLocalization();

  const apiRef = useGridApiRef();
  const {initialState, saveState} = useDataGridSettings(
    agentWorkSummarySchema.name,
    {storeSize: true, storeSorting: true},
  );
  const {paginationModel, onPaginationModelChange} = useDataTablePagination(
    apiRef,
    initialState?.pagination?.paginationModel,
  );

  const [sortItem, setSortItem] = useState<GridSortModel[number] | undefined>();
  const handleSortModelChange = useCallback((model: GridSortModel) => {
    setSortItem(model[0]);
  }, []);

  const {data, isLoading, total} = useAgentWorks(
    agent,
    paginationModel.page,
    paginationModel.pageSize,
    sortItem?.field,
    sortItem?.sort ?? undefined,
    !!agent?.id,
  );

  return (
    <Table
      header={t('data.header.relations')}
      schema={agentWorkSummarySchema}
      loading={isLoading}
      data={data}
      rowCount={total}
      configuration={agentWorkSummaryTableConfig}
      onRowClick={onRowClick}
      apiRef={apiRef}
      initialState={initialState}
      onSaveState={saveState}
      onPaginationModelChange={onPaginationModelChange}
      onSortModelChange={handleSortModelChange}
    />
  );
};
